import React, { useState } from 'react';
import styled from 'App/styled';

import ShoppingBagForHeaderIcon from 'components/Icons/Bag';
import { useGamoogaData } from '../Notification';
import TooltipContent from './TooltipContent';
import { handleCartNotificationOnclick } from '../Notification/helpers';
import Tooltip from 'components/Tooltip';
import { getAppTheme } from 'App/theme';
import { useIsConfigHeader, useIsThemeHeader } from 'hooks/header';
import {
  getBgColor,
  getStyles,
  shouldShowGamoogaNotifiction,
} from 'components/Header/helper';
import { GamoogaNotifRestriction } from '../../types';
import { useSelector } from 'react-redux';
import { getEligibleForVariant } from 'helpers/experimentV2';
import { State } from 'store/types';
import { AB_DISMISSABLE_TOOLTIP } from 'constants/experiments';
import { useIsAddressBarEnabled } from 'hooks/header';
import { pushEvent } from '@nykaa/data-layer';
import { webViewCallbackhandler } from '@nykaa/webview-wizard';
import { webViewCallbacks } from '@nykaa/webview-wizard/constant';
import { isDeviceNotWeb } from 'helpers/utils/device';

const Button = styled.button<{
  isLight: boolean;
  headerBg?: string;
  headerTextColor?: string;
  isAddressBarEnabled?: boolean;
}>`
  border: none;
  border-radius: 50%;
  position: relative;
  width: 48px;
  height: 48px;
  background: ${({ headerBg, isAddressBarEnabled }) =>
    isAddressBarEnabled
      ? 'transparent'
      : getBgColor('rgba(255, 255, 255, 0.5)', headerBg)};
  ${({ isLight, isAddressBarEnabled }) =>
    !isAddressBarEnabled &&
    isLight &&
    `
    background: transparent;
  `};

  .cart-count {
    ${({ theme }) => theme.typography.labelSmall};
    display: flex;
    width: 23px;
    height: 16px;
    border-radius: 8px;
    border: solid 1.5px #ffffff;
    line-height: normal;
    position: absolute;
    top: 6px;
    right: 2px;
    justify-content: center;
    align-items: center;
    background: ${(props) => props.theme.brandPrimary};
    color: #fff;
    ${({ isLight, headerTextColor, headerBg, theme: { colors } }) =>
      getStyles(isLight, headerTextColor, headerBg, colors)};
  }
`;

const TOOLTIP_CLOSED = 'cart_icon_tooltip_closed';

interface ShoppingBagProps {
  cartCount: number;
  cartViewLocation?: string;
  buttonId?: string;
  gamoogaNotifRestriction?: GamoogaNotifRestriction;
  pageSource?: string;
  isConvSearch?: boolean;
}
function ShoppingBag({
  cartCount,
  cartViewLocation,
  buttonId,
  gamoogaNotifRestriction,
  pageSource,
  isConvSearch,
}: ShoppingBagProps) {
  const theme = getAppTheme();
  let gamoogaCartNotification = useGamoogaData();
  const isThemeHeader = useIsThemeHeader();
  const [hideTooltip, setHideTooltip] = useState(false);
  const { headerBg, headerTextColor } = useIsConfigHeader();
  const isDimissableTooltip = useSelector((state: State) =>
    getEligibleForVariant(state, AB_DISMISSABLE_TOOLTIP, 'A')
  );
  const isAddressBarEnabled = useIsAddressBarEnabled();

  const device = useSelector((state: State) => state.appReducer?.client.device);

  const handleClick = () => {
    if (isDeviceNotWeb(device) && isConvSearch) {
      const appParams = {
        device,
        callbackFunctionName: webViewCallbacks.showCart,
        callbackParams: '',
      };
      webViewCallbackhandler(appParams);
      return;
    }
    handleCartNotificationOnclick({
      gamoogaCartNotification,
      cartViewLocation,
      pageSource,
    });
  };

  const handleCrossClick = () => {
    setHideTooltip(true);
    pushEvent(TOOLTIP_CLOSED);
  };

  const getHiddenCondition = () => {
    if (isDimissableTooltip) {
      return !gamoogaCartNotification?.str || hideTooltip;
    } else {
      return !gamoogaCartNotification?.str;
    }
  };

  const showNotification = shouldShowGamoogaNotifiction(
    gamoogaNotifRestriction
  );

  if (!showNotification) {
    gamoogaCartNotification = null;
  }

  const customStyles = {
    ...theme.typography.bodySmall,
    backgroundColor: theme.colors.primary50,
    color: theme.colors.surface,
    textAlign: 'center',
    borderRadius: 8,
    zIndex: 2,
    padding: 8,
    whiteSpace: 'nowrap',
  };

  const newCustomStyles = {
    ...theme.typography.bodySmall,
    backgroundColor: theme.colors.surfaceInverse30,
    color: theme.colors.surface,
    borderRadius: theme.borders.radius30,
    zIndex: 2,
    padding: `${theme.spacing.spacing40} ${theme.spacing.spacing60}`,
    width: '203px',
  };
  return (
    <Tooltip
      isHidden={getHiddenCondition()}
      style={isDimissableTooltip ? newCustomStyles : customStyles}
      isDimissableTooltip={isDimissableTooltip}
      content={() => (
        <TooltipContent
          config={gamoogaCartNotification}
          isDimissableTooltip={isDimissableTooltip}
          handleTooltipClose={handleCrossClick}
        />
      )}
    >
      <Button
        isLight={isThemeHeader}
        headerTextColor={headerTextColor}
        type="button"
        onClick={handleClick}
        id={buttonId}
        headerBg={headerBg}
        isAddressBarEnabled={isAddressBarEnabled}
        aria-label={`Cart item${cartCount !== 1 ? 's' : ''} ${cartCount}`}
      >
        <ShoppingBagForHeaderIcon
          isLight={isThemeHeader}
          color={headerTextColor}
        />
        {cartCount > 0 && (
          <span
            className="cart-count"
            aria-hidden="true"
          >
            {cartCount}
          </span>
        )}
      </Button>
    </Tooltip>
  );
}

export default ShoppingBag;
